<app-split-pane
  storageKey="split.architecture"
  [initialLeftRatio]="0.7"
  [collapseBreakpointPx]="1024"
  [collapsedOnSmallScreens]="true"
>
  <ng-container left>
    @if (!architectureStore.activeProjectId() || architectureStore.loading()) {
      <app-split-pane-vertical storageKey="split.architecture.left" [initialTopRatio]="0.7">
        <ng-container top>
          <div class="p-3">
            <div class="card border-0 shadow-sm p-3">
              <app-skeleton-block height="14px" width="44%" rounded="8px"></app-skeleton-block>
              <div class="mt-3"></div>
              <app-skeleton-block [lines]="6" height="12px" width="100%" rounded="8px" gap="10px"></app-skeleton-block>
              <div class="mt-3"></div>
              <app-skeleton-block height="44px" width="100%" rounded="10px"></app-skeleton-block>
            </div>
          </div>
        </ng-container>
        <ng-container bottom>
          <div class="p-3">
            <div class="card border-0 shadow-sm p-3">
              <app-skeleton-block height="14px" width="30%" rounded="8px"></app-skeleton-block>
              <div class="mt-3"></div>
              <app-skeleton-block [lines]="5" height="12px" width="100%" rounded="8px" gap="10px"></app-skeleton-block>
            </div>
          </div>
        </ng-container>
      </app-split-pane-vertical>
    } @else {
      <app-split-pane-vertical storageKey="split.architecture.left" [initialTopRatio]="0.7">
        <ng-container top>
          <div class="architecture-chat-pane">
            @if (canShowGenerateFromRepoButton) {
              <button class="btn btn-sm btn-outline-primary architecture-repo-generate-btn" type="button" (click)="onGenerateFromRepo()">
                <i class="bi bi-github me-1"></i>
                Generate from repo
              </button>
            }
            <app-chat
              #architectureChat
              [conversationId]="architectureStore.activeProjectId()"
              [suggestions]="architectureSuggestions"
              [context]="architectureContext"
              [orchestratorPageContext]="architectureOrchestratorContext"
              [initialMessages]="architectureStore.chatMessages()"
              (messagesChange)="onChatMessagesChange($event)"
              (messageCreated)="onChatMessageCreated($event)"
              (aiResult)="onAiResult($event)"
              placeholder="Describe your system components and how they interact..."
            ></app-chat>
          </div>
        </ng-container>
        <ng-container bottom>
          <app-history scopeKey="architecture.chat"></app-history>
        </ng-container>
      </app-split-pane-vertical>
    }
  </ng-container>

  <ng-container right>
    <app-split-pane-vertical storageKey="split.architecture.right" [initialTopRatio]="0.85">
      <ng-container top>
        <div class="architecture-container">
          <section class="card arch-decisions-card border-0 shadow-sm p-3 p-md-4">
            <h2 class="h4 mb-1">Architecture</h2>
            <p class="text-muted mb-3">
              Capture key architecture decisions before refining the system diagram.
            </p>

            <h3 class="h5 mb-3">Architecture Decisions</h3>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Pattern</label>
                <select class="form-select" [(ngModel)]="architectureDecisionDraft.pattern">
                  <option value="">Select pattern...</option>
                  @for (option of architecturePatternOptions; track option) {
                    <option [value]="option">{{ option }}</option>
                  }
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Deployment model</label>
                <select class="form-select" [(ngModel)]="architectureDecisionDraft.deploymentModel">
                  <option value="">Select deployment model...</option>
                  @for (option of deploymentModelOptions; track option) {
                    <option [value]="option">{{ option }}</option>
                  }
                </select>
              </div>
              <div class="col-12">
                <label class="form-label">Integration notes</label>
                <textarea
                  class="form-control"
                  rows="3"
                  [(ngModel)]="architectureDecisionDraft.integrationNotes"
                  placeholder="List external integrations, event flows, or API contracts..."
                ></textarea>
              </div>
            </div>

            <div class="mt-3 d-flex align-items-center gap-2">
              <button
                class="btn btn-primary"
                type="button"
                [disabled]="applyingDecisions"
                (click)="applyArchitectureDecisions()"
              >
                @if (applyingDecisions) {
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Applying...
                } @else {
                  Apply Architecture
                }
              </button>
            </div>

            @if (decisionsValidationError) {
              <div class="alert alert-danger mt-3 mb-0">{{ decisionsValidationError }}</div>
            }
            @if (decisionsSuccessMessage) {
              <div class="alert alert-success mt-3 mb-0">{{ decisionsSuccessMessage }}</div>
            }

            <div class="system-outline mt-3">
              <h4 class="h6 mb-2">System Outline</h4>
              @if (!hasAppliedDecisions) {
                <div class="small text-muted">
                  No outline yet. Apply decisions to populate a baseline architecture summary.
                </div>
              } @else {
                <div class="row g-2">
                  <div class="col-md-4">
                    <article class="outline-card">
                      <div class="outline-label">Pattern</div>
                      <div class="outline-value">{{ architectureDecisionDraft.pattern }}</div>
                    </article>
                  </div>
                  <div class="col-md-4">
                    <article class="outline-card">
                      <div class="outline-label">Deployment</div>
                      <div class="outline-value">{{ architectureDecisionDraft.deploymentModel }}</div>
                    </article>
                  </div>
                  <div class="col-md-4">
                    <article class="outline-card">
                      <div class="outline-label">Integrations</div>
                      <div class="outline-value">
                        {{ architectureDecisionDraft.integrationNotes.trim() || 'No notes provided.' }}
                      </div>
                    </article>
                  </div>
                </div>
              }
            </div>
          </section>

          <div class="arch-panel">
            <div class="arch-header">
              <div class="arch-header__title">
                Architecture
                <span class="hint">Pan: drag • Zoom: Ctrl + scroll • Rename: double-click</span>
              </div>
              <div class="arch-toolbar" role="toolbar" aria-label="Architecture tools">
                <div class="btn-group btn-group-sm" role="group" aria-label="Ink tool mode">
                  <button
                    class="btn"
                    [class.btn-primary]="toolMode === 'select'"
                    [class.btn-outline-secondary]="toolMode !== 'select'"
                    type="button"
                    (click)="toolMode = 'select'"
                  >
                    <i class="bi bi-cursor"></i>
                  </button>
                  <button
                    class="btn"
                    [class.btn-primary]="toolMode === 'draw'"
                    [class.btn-outline-secondary]="toolMode !== 'draw'"
                    type="button"
                    (click)="toolMode = 'draw'"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                    class="btn"
                    [class.btn-primary]="toolMode === 'erase'"
                    [class.btn-outline-secondary]="toolMode !== 'erase'"
                    type="button"
                    (click)="toolMode = 'erase'"
                  >
                    <i class="bi bi-eraser"></i>
                  </button>
                </div>
                <input
                  class="form-control form-control-sm arch-color"
                  type="color"
                  [value]="inkColor"
                  (input)="inkColor = ($any($event.target).value)"
                  aria-label="Ink color"
                />
                <input
                  class="form-range arch-size"
                  type="range"
                  min="4"
                  max="26"
                  step="1"
                  [value]="inkSize"
                  (input)="inkSize = +($any($event.target).value)"
                  aria-label="Ink size"
                />
                <button class="btn btn-sm btn-outline-secondary" type="button" (click)="onAddNode('service')">
                  <i class="bi bi-box"></i>
                  Add service
                </button>
                <button class="btn btn-sm btn-outline-secondary" type="button" (click)="onAddNode('database')">
                  <i class="bi bi-database"></i>
                  Add database
                </button>
                <button class="btn btn-sm btn-outline-secondary" type="button" (click)="onTidyLayout()">
                  <i class="bi bi-magic"></i>
                  Tidy
                </button>
              </div>
            </div>

            <div #archCanvas class="arch-canvas" aria-label="Architecture canvas">
              <app-ink-layer
                [graph]="graphInstance"
                [mode]="toolMode"
                [color]="inkColor"
                [size]="inkSize"
                [strokes]="architectureStore.ink()?.strokes ?? []"
                (strokesChange)="onInkStrokesChange($event)"
              ></app-ink-layer>
              @if (!architectureStore.activeProjectId() || architectureStore.loading()) {
                <div class="arch-loading-overlay" aria-label="Loading architecture">
                  <div class="arch-loading-card">
                    <app-skeleton-block height="14px" width="38%" rounded="8px"></app-skeleton-block>
                    <div class="mt-3"></div>
                    <app-skeleton-block [lines]="4" height="12px" width="100%" rounded="8px" gap="10px"></app-skeleton-block>
                    <div class="mt-3"></div>
                    <app-skeleton-block height="220px" width="100%" rounded="12px"></app-skeleton-block>
                  </div>
                </div>
              } @else {
                @if (!architectureStore.diagram()) {
                  <div class="arch-empty-overlay">
                    <div class="arch-empty-card">
                      <div class="fw-semibold mb-1">No diagram yet</div>
                      <div class="small text-muted">
                        Use the chat to scaffold your architecture, or add nodes with the toolbar.
                      </div>
                    </div>
                  </div>
                }
              }
            </div>

            <!-- Angular template used for X6 angular-shape nodes -->
            <ng-template #archNode let-data="ngArguments">
              <div class="arch-node" [style.--arch-accent]="data.accent || 'var(--primary-color)'">
                <div class="arch-node__badge">
                  @switch (data.kind) {
                    @case ('database') { <i class="bi bi-database"></i> }
                    @case ('queue') { <i class="bi bi-arrow-repeat"></i> }
                    @case ('external') { <i class="bi bi-globe2"></i> }
                    @case ('client') { <i class="bi bi-person-badge"></i> }
                    @case ('boundary') { <i class="bi bi-shield-lock"></i> }
                    @default { <i class="bi bi-box"></i> }
                  }
                </div>
                <div class="arch-node__title">{{ data.title }}</div>
                <div class="arch-node__meta">{{ data.kind || 'service' }}</div>
              </div>
            </ng-template>
          </div>
        </div>
      </ng-container>
      <ng-container bottom>
        <app-history scopeKey="architecture.main"></app-history>
      </ng-container>
    </app-split-pane-vertical>
  </ng-container>
</app-split-pane>

